import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link as RouterLink, useHistory } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import SectionTitle from 'components/section-title'
import { TonalButton } from 'components/empty-page-state'
import NotificationsActiveIcon from 'components/material-symbol/icons/NotificationsActive'
import MessageOutlinedIcon from 'components/material-symbol/icons/Message'
import StreamSendMessageButton from 'components/stream-messaging/send-message-button'
import { FOLLOW_UP_LIST_PATH } from 'components/student-insights/constants'
import RetentionStatusChip from 'components/student-insights/retention-status-chip'
import { useStudentInsightsSummary } from 'components/student-insights/api-hooks'
import { followUpSearch } from 'components/student-insights/chat-follow-up-helper'
import { amplitudeEvent } from 'lib/amplitude'
import { useAuth } from 'lib/auth'
import useStyles from './styles'

export const FOLLOW_UP_LIMIT = 5

const studentPath = id => `/pga-coach/student/${id}`

export const StudentsToFollowUpView = ({
  insights,
  totalCount,
  loading,
  canMessage,
  className,
}) => {
  const classes = useStyles()
  const history = useHistory()

  return (
    <Box className={`${classes.card} ${className || ''}`}>
      <Box className={classes.cardHeader}>
        <Box className={classes.cardHeaderLeft}>
          <Box className={classes.iconCircle}>
            <NotificationsActiveIcon />
          </Box>
          <SectionTitle className={classes.cardTitle}>
            Students to follow up with
          </SectionTitle>
        </Box>
        {totalCount > 0 ? (
          <Button
            component={RouterLink}
            to={FOLLOW_UP_LIST_PATH}
            className={classes.cardAction}
          >
            View all {totalCount}
          </Button>
        ) : null}
      </Box>

      {loading ? (
        <LinearProgress color="primary" className={classes.loading} />
      ) : insights.length > 0 ? (
        <Box className={classes.list}>
          {insights.map(({ student, status, reason }) => {
            const openStudent = () => history.push(studentPath(student.id))
            const contactId = student.contact?.externalId

            return (
              <Box
                key={student.id}
                className={classes.row}
                onClick={openStudent}
                role="button"
                tabIndex={0}
                onKeyDown={event => {
                  if (event.target !== event.currentTarget) return
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    openStudent()
                  }
                }}
              >
                <Box className={classes.rowMain}>
                  <Typography component="span" className={classes.rowName}>
                    {student.firstName} {student.lastName}
                    <RetentionStatusChip status={status} />
                  </Typography>
                  <Typography component="div" className={classes.rowMeta}>
                    {reason}
                  </Typography>
                </Box>
                {canMessage && contactId ? (
                  <Box className={classes.rowTrailing}>
                    <StreamSendMessageButton
                      userIds={[contactId]}
                      search={followUpSearch(student.id)}
                      component={TonalButton}
                      size="small"
                      className={classes.replyButton}
                      startIcon={<MessageOutlinedIcon />}
                      onClick={event => {
                        event.stopPropagation()
                        amplitudeEvent('student-insights-follow-up-opened', {
                          source: 'dashboard',
                        })
                      }}
                    >
                      Follow Up
                    </StreamSendMessageButton>
                  </Box>
                ) : null}
              </Box>
            )
          })}
        </Box>
      ) : (
        <Typography className={classes.emptyList}>
          You're all caught up. Students who cancel, are new, or haven't booked
          in a while show up here.
        </Typography>
      )}
    </Box>
  )
}

StudentsToFollowUpView.propTypes = {
  insights: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string.isRequired,
      reason: PropTypes.string,
      student: PropTypes.shape({
        id: PropTypes.string.isRequired,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        contact: PropTypes.shape({ externalId: PropTypes.string }),
      }).isRequired,
    }),
  ).isRequired,
  totalCount: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  canMessage: PropTypes.bool,
  className: PropTypes.string,
}

StudentsToFollowUpView.defaultProps = {
  canMessage: true,
}

const StudentsToFollowUp = ({ className }) => {
  const { user } = useAuth()
  const { data, loading, error } = useStudentInsightsSummary(FOLLOW_UP_LIMIT)
  const summary = data?.studentInsightsSummary

  useEffect(() => {
    if (error) {
      window.rg4js &&
        window.rg4js('send', { error, tags: ['student-insights'] })
    }
  }, [error])

  if (data ? !summary : !loading) return null

  return (
    <StudentsToFollowUpView
      className={className}
      insights={summary?.needsAttention || []}
      totalCount={summary?.needsAttentionCount || 0}
      loading={loading && !data}
      canMessage={!user?.hideSensitiveInformation}
    />
  )
}

StudentsToFollowUp.propTypes = {
  className: PropTypes.string,
}

export default StudentsToFollowUp
